import { useCallback, useMemo, useRef, useState } from 'react'
import { Separator } from '../../../components/separator'
import { HouseholdFormData, ReasonFormData } from '../../../models/classes/ApplicationStep1.class'

import HouseholdStructureTable from '../../../components/table/household-structure-table'

import OwnerOccupierSelection from '../../../components/HOC/owner-occupier-selection'
import { Owner } from '../../../models/polymer/Owner.model'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { Helpers } from '../../../utils/helper'
import { OwnershipReasonEnum } from '../../../models/enum/ownership-reason.enum'
import { StructureStatusEnum } from '../../../models/enum/structure-status.enum'
import { Occupier } from '../../../models/polymer/Occupier.model'
import { cloneDeep, isUndefined } from 'lodash'
import { HoldingFormData } from '../../../models/polymer/HoldingFormData.model'
import { HoldingOwner } from '../../../models/polymer/HoldingOwner.model'
import { HoldingOccupier } from '../../../models/polymer/HoldingOccupier.model'
import { ReasonEnum } from '../../../models/enum/reason.enum'
import { MyInfo, MyInfoWithDOD } from '../../../models/polymer/MyInfo.model'
import NewOwnerAccordion from '../../../components/HOC/new-owner-accordion'
import NewOccupierAccordion from '../../../components/HOC/new-occupier-accordion'
import { P2Modal, P2TextDescription, P2TextLabel } from 'react-bootstrap-common-script'
import { SexEnum } from '../../../models/enum/sex.enum'
import { RaceType } from '../../../models/enum/race.enum'
import { NationalityType } from '../../../models/enum/nationality.enum'
import { convertRaceToEthnicCode } from '../../../utils/form-helper'
import { useAppSelector } from '../../../redux/hook'
import { IDTypeEnum } from '../../../models/enum/id-type.enum'
import { MaritalStatusEnum } from '../../../models/enum/marital-status.enum'
import { ResidentialStatusEnum } from '../../../models/enum/residential-status.enum'
interface HouseholdStructureProps {
  description?: string,
  reasonValues: ReasonFormData,
  householdFormValues: HouseholdFormData,
  householdSetFormValue?: (update: HouseholdFormData) => void;
  holdingFormValues: HoldingFormData
  holdingFormErrors: any
  holdingSetFormValue?: (update: HoldingFormData) => void;
  currentOwnerMyInfoData: MyInfo;
  retrieveFromMyInfo: boolean | null;
  loginUserStructureStatus?: string;
  forceError?: boolean;
}


function HouseholdStructure({...props }: HouseholdStructureProps) {
  const ownerNodeRef = useRef(null);
  const occupierNodeRef = useRef(null);

  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);
  const applicationStartFormData = useAppSelector(state => state.app.ApplicationStartSlice.formData);
  const loginUser = useAppSelector(state => state.auth.AuthenticationSlice.loginUser);

  const DynamicDescription = useMemo(() => {
    if ( props.reasonValues.reason === ReasonEnum.marital_status_reason_value || props.reasonValues.reason === ReasonEnum.loss_of_citizenship_reason_value || props.reasonValues.reason === ReasonEnum.owner_moving_out_reason_value ) {
      if ( props.reasonValues.change_ownership_reason === OwnershipReasonEnum.inclusion_due_to_marriage ) {
        return "Based on the selected reason, at least 1 of the current owner(s) should be included in the proposed household and there must also be new owner(s) added.";
      }
      return "Based on the selected reason, you are unable to add new owner(s) when all the current owner(s) are also included in the proposed household.";
    } else if ( props.reasonValues.reason === ReasonEnum.financial_circumstances_reason_value ) {
      return "Based on the selected reason, you must add at least 1 new owner(s) in the proposed household.";
    } else {
      return "";
    }
  }, [props.reasonValues]);

  const [defaultCloseArr, setDefaultCloseArr] = useState<string[]>([]);
  const [defaultCloseOccupiersArr, setDefaultCloseOccupiersArr] = useState<string[]>([]);

  const anchorRef = useRef<HTMLDivElement>(null);

  const scrollToTable = useCallback(() => {
    window.scrollTo(0, anchorRef.current?.offsetTop ?? 0);
  }, [anchorRef]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [getOwnerName, setGetOwnerName] = useState(String);
  const [deleteOwner, setDeleteOwner] = useState(-1);
  const [currentOwner, setCurrentOwner] = useState(Number);
  const [accordionType, setAccordionType] = useState<"owner" | "occupier">("owner");

  const createOnRemoveHandler = (index: number, ownerName: string, type: "owner" | "occupier")=> () => {
    setDeleteOwner(index);
    let displayName;
    if (ownerName.length > 0) {
        displayName = ownerName;
    } else {
        displayName = type === "owner" ? "New Owner" : "New Occupier";
    }
    setGetOwnerName(displayName);
    setCurrentOwner(index);
    setAccordionType(type);
    setIsModalOpen(true);
  };
  


  /**
   * onAddHouseholdIncomingOwner : add the incoming_owners from props householdFormValues passing from application-step-1.
   */
  const onAddHouseholdIncomingOwner = useCallback((e?: HoldingOwner) => {

    let foundUser: HoldingOwner | undefined = props.holdingFormValues?.holding_owners?.find(owner => owner.id === e?.id);

    let incomingNewUser = new Owner(foundUser);

    if (foundUser?.type === "existing") {
      if(foundUser?.existing_type === "owner"){
        incomingNewUser.structure_status = StructureStatusEnum.retaining;
      }else if(foundUser?.existing_type === "occupier" || foundUser?.existing_type === "additional"){
        incomingNewUser.structure_status = StructureStatusEnum.incoming;
      }
    }
    else if (foundUser?.type === "new") {
      incomingNewUser.structure_status = StructureStatusEnum.incoming;
    }

    if (props.householdSetFormValue && foundUser) {
      props.householdSetFormValue({ ...props.householdFormValues, incoming_owners: [...props.householdFormValues.incoming_owners, incomingNewUser] })
    }

    scrollToTable();

  }, [props.holdingFormValues?.holding_owners, props])

  const onAddHouseholdIncomingOccupier = useCallback((e?: HoldingOccupier) => {
    let foundUser: HoldingOccupier | undefined = props.holdingFormValues?.holding_occupiers?.find(owner => owner.id === e?.id);

    let incomingNewUser = new Occupier(foundUser);

    if (foundUser?.type === "existing") {
      if(foundUser.existing_type === "owner" || foundUser.existing_type === "additional"){
          incomingNewUser.structure_status = StructureStatusEnum.incoming;
      }else if(foundUser.existing_type === "occupier"){
        incomingNewUser.structure_status = StructureStatusEnum.retaining;
      }
    }
    else if (foundUser?.type === "new") {
      incomingNewUser.structure_status = StructureStatusEnum.incoming;
    }

    if (props.householdSetFormValue && foundUser) {
      props.householdSetFormValue({ ...props.householdFormValues, incoming_occupiers: [...props.householdFormValues.incoming_occupiers, incomingNewUser] })
    }

    scrollToTable();

  }, [props.holdingFormValues?.holding_occupiers, props])



  /**
   * onUpdateHouseholdIncomingOwner: update the info from holdingFormValue to householdFormValue
   */
  const onUpdateHouseholdIncomingOwner = useCallback((e?: HoldingOwner) => {
    const temp_incoming_owners = props.householdFormValues?.incoming_owners;
    let new_owners = temp_incoming_owners?.map(incoming_owner => {
      if (incoming_owner.myinfo?.uinfin === (e?.original_uinfin ?? e?.myinfo?.uinfin)) {
        let incoming_structure_status = incoming_owner.structure_status;
        let newIncomingOwner = new Owner({...e, structure_status:incoming_structure_status});
        return newIncomingOwner;
      }
      else {
        return incoming_owner;
      }
    })
    if (props.householdSetFormValue) {
      props.householdSetFormValue({ ...props.householdFormValues, incoming_owners: isUndefined(new_owners) ? [] : new_owners })
    }
    scrollToTable();
  }, [props.holdingFormValues, props.householdFormValues]);


  const onUpdateHouseholdIncomingOccupier = useCallback((e?: HoldingOccupier) => {
    const temp_incoming_occupiers = props.householdFormValues?.incoming_occupiers;
    let new_occupiers = temp_incoming_occupiers?.map(incoming_owner => {
      if (incoming_owner.myinfo?.uinfin === (e?.original_uinfin ?? e?.myinfo?.uinfin)) {
        let incoming_structure_status = incoming_owner.structure_status;

        let newIncomingOccupier = new Occupier({...e, structure_status:incoming_structure_status});
        return newIncomingOccupier;
      }
      else {
        return incoming_owner;
      }
    })
    if (props.householdSetFormValue) {
      props.householdSetFormValue({ ...props.householdFormValues, incoming_occupiers: isUndefined(new_occupiers) ? [] : new_occupiers })
    }
    scrollToTable();
  }, [props.holdingFormValues, props.householdFormValues]);


  /**
   * handleOnToggleOwner: track the open/close accordion.
   * So when remove, then the accordion will remain as it it.
   */
  const handleOnToggleOwner = useCallback((isOpen: boolean, id?: string) => {
    if (id) {
      if (!isOpen) {
        setDefaultCloseArr([...defaultCloseArr, id]);
      }
      else {
        let tempArr = [...defaultCloseArr];
        let findIndex = props.holdingFormValues?.holding_owners?.findIndex(owner => owner.id === id);
        if (findIndex !== undefined && findIndex !== -1) {
          tempArr.splice(findIndex, 1);
        }
        setDefaultCloseArr(tempArr);
      }
    }
  }, [defaultCloseArr, props.holdingFormValues]);

  const handleOnToggleOccupier = useCallback((isOpen: boolean, id?: string) => {
    if (id) {
      if (!isOpen) {
        setDefaultCloseOccupiersArr([...defaultCloseArr, id]);
      }
      else {
        let tempArr = [...defaultCloseArr];
        let findIndex = props.holdingFormValues?.holding_occupiers?.findIndex(owner => owner.id === id);
        if (findIndex !== undefined && findIndex !== -1) {
          tempArr.splice(findIndex, 1);
        }
        setDefaultCloseOccupiersArr(tempArr);
      }
    }
  }, [defaultCloseArr, props.holdingFormValues]);



  const holdingIncomingOwnerHandler = useCallback((id: string, values: string | boolean, type: string) => {
    let new_array = cloneDeep([...props.holdingFormValues.holding_owners]);
    const owner = new_array.find(item => item.id === id);
    if(!owner){
      return;
    }
    if (type === "residential_status") {
      owner.myinfo.residential_status = values.toString();
      if(owner.myinfo.residential_status === ResidentialStatusEnum.citizen && loginUser.uinfin === owner.myinfo.uinfin){
        owner.myinfo.nationality = "SG";
      }

      if(values.toString() !== ResidentialStatusEnum.pr){
          owner.spr_obtained_date = "";
      }
    }
    else if (type === "children_care_and_control") {
     owner.children_care_and_control= values.toString();
    }
    else if (type === "name") {
      owner.myinfo.name = values.toString();
    }
    else if (type === "uinfin") {
      owner.myinfo.uinfin = values.toString().toUpperCase();
      if(owner.myinfo.residential_status === ResidentialStatusEnum.citizen && loginUser.uinfin === values.toString().toUpperCase()){
        owner.myinfo.nationality = "SG";
      }
    }
    else if (type === "is_related") {
      owner.is_related = values as boolean;
    }
    else if (type === "relationship_with_owner") {
     owner.relationship_with_owner = values.toString();
    }
    else if (type === "dob") {
      owner.myinfo.dob = values.toString();
    }
    else if (type === "marital_status") {
      owner.myinfo.marital_status = values.toString();
      if(owner.myinfo.marital_status !== MaritalStatusEnum.married){
        //Reset spouse id and id type when not married are selected
        owner.spouse = "";
        owner.spouse_id_type = undefined;
      }

      if(owner.myinfo.marital_status !== MaritalStatusEnum.divorced){
        //Reset control and care of children
        owner.children_care_and_control = null;
      }
    }
    else if (type === "spr_obtained_date") {
      owner.spr_obtained_date = values.toString();
    }
    else if (type === "gross_monthly_income") {
      owner.gross_monthly_income = values.toString();
    } else if (type === "sex") {
      owner.myinfo.sex = values.toString() as SexEnum;
    } else if (type === "race") {
      owner.myinfo.race = values.toString() as RaceType;
    } else if (type === "nationality") {
      owner.myinfo.nationality = values.toString() as NationalityType;
    }
    else if (type === "spouse") {
      owner.spouse = values.toString();
    }
    else if (type === "spouse_id_type") {
      owner.spouse_id_type = values.toString() as IDTypeEnum;
    }
    if (props.holdingSetFormValue) {
      props.holdingSetFormValue({ ...props.holdingFormValues, holding_owners: new_array })
    }
  },[props])

  const holdingIncomingOccupierHandler = useCallback((id: string, values: string | boolean, type: string) => {
    let new_array: HoldingOccupier[] = cloneDeep([...props.holdingFormValues.holding_occupiers]);
    const occupier = new_array.find(item => item.id === id);
    if(!occupier){
      return;
    }
    if (type === "residential_status") {
      occupier.myinfo.residential_status = values.toString();
      if(occupier.myinfo.residential_status === ResidentialStatusEnum.citizen){
        occupier.myinfo.nationality = "SG";
      }
    }
    else if (type === "name") {
      occupier.myinfo.name = values.toString();
    }
    else if (type === "uinfin") {
      occupier.myinfo.uinfin = values.toString().toUpperCase();
      if(occupier.myinfo.residential_status === ResidentialStatusEnum.citizen){
        occupier.myinfo.nationality = "SG";
      }
    }
    else  if (type === "id_type"){
      occupier.id_type = values as IDTypeEnum;
    }
    else if (type === "is_related") {
      occupier.is_related = values as boolean;
    }
    else if (type === "relationship_with_owner") {
      occupier.relationship_with_owner = values.toString();
      if (occupier.relationship_with_owner !== "") {
        occupier.is_related = true
      }
    }
    else if (type === "dob") {
      occupier.myinfo.dob = values.toString();
    }
    else if (type === "marital_status") {
     occupier.myinfo.marital_status = values.toString();
     if(occupier.myinfo.marital_status !== MaritalStatusEnum.married){
      //Reset spouse id and id type when not married are selected
      occupier.spouse = "";
      occupier.spouse_id_type = undefined;
    }
    }
    else if (type === "spouse") {
      occupier.spouse = values.toString();
    }
    else if (type === "spouse_id_type") {
      occupier.spouse_id_type = values.toString() as IDTypeEnum;
    }
    else if (type === "sex") {
      occupier.myinfo.sex = values.toString() as SexEnum;
    }
    else if (type === "nationality") {
      occupier.myinfo.nationality = values.toString() as NationalityType;
    }else if (type === "race") {
      occupier.myinfo.race = values.toString() as RaceType;
    }
    if (props.holdingSetFormValue) {
      props.holdingSetFormValue({ ...props.holdingFormValues, holding_occupiers: new_array })
    }
  },[props])



  /**
   * onRemoveOwnerHolding: remove the accordion based on the index of the .map
   */
  const onRemoveOwnerHolding = useCallback((_index: number) => {

    let tempHoldingOwners = props.holdingFormValues.holding_owners;
    let tempIncomingHouseholds = props.householdFormValues?.incoming_owners;

    if (_index === 0 && tempHoldingOwners.length >= 1) {
      if (props.holdingSetFormValue) {
        props.holdingSetFormValue({ ...props.holdingFormValues, holding_owners: [], holding_occupiers: [] });
      }
      if (props.householdSetFormValue) {
        //When owner removed, reset incoming owners and incoming occupiers
        props.householdSetFormValue({ ...props.householdFormValues, incoming_owners: [], incoming_occupiers: [] });
      }
    } else {
      if (props.holdingSetFormValue) {
        tempHoldingOwners.splice(_index, 1);
        props.holdingSetFormValue({ ...props.holdingFormValues, holding_owners: [...tempHoldingOwners] });
      }

      if (props.householdSetFormValue) {
        tempIncomingHouseholds?.splice(_index, 1);
        props.householdSetFormValue({ ...props.householdFormValues, incoming_owners: [...tempIncomingHouseholds] });
      }
    }

  }, [props])

  const onRemoveOccupierHolding = useCallback((_index: number) => {

    let tempHoldingOccupiers = props.holdingFormValues.holding_occupiers;
    let tempIncomingHouseholds = props.householdFormValues?.incoming_occupiers;

    if (_index === 0 && tempHoldingOccupiers.length > 1) {
      if (props.holdingSetFormValue) {
        props.holdingSetFormValue({ ...props.holdingFormValues, holding_occupiers: [] });
      }
      if (props.householdSetFormValue) {
        props.householdSetFormValue({ ...props.householdFormValues, incoming_occupiers: [] });
      }
    }
    else {
      if (props.holdingSetFormValue) {
        tempHoldingOccupiers.splice(_index, 1);
        props.holdingSetFormValue({ ...props.holdingFormValues, holding_occupiers: [...tempHoldingOccupiers] });
      }

      if (props.householdSetFormValue) {
        tempIncomingHouseholds?.splice(_index, 1);
        props.householdSetFormValue({ ...props.householdFormValues, incoming_occupiers: [...tempIncomingHouseholds] })
      }
    }

  }, [props])



  /**
    * addOwnerHolding: add owner/occupier to the holding
    */
  const addOwnerHolding = useCallback((nric: string) => {
    /**
    * Value can be uinfin or add_new
    * - add_new will add the add_new card fields to users to enter info
    */
    let tempHoldingOwner: HoldingOwner = new HoldingOwner();
    let foundUser: Owner | undefined = props?.householdFormValues?.owners?.find(e => e.myinfo?.uinfin === nric);
    let foundOccupierUser: Occupier | undefined = props?.householdFormValues?.occupiers?.find(e => e.myinfo?.uinfin === nric); //When occupier are selected from the owner dropdown selection
    let temp = props.holdingFormValues?.holding_owners.filter(e => e.is_proposed === true); //to remove any unconfirmed box
    let foundAdditionalRole: MyInfoWithDOD | undefined = applicationStartFormData?.selected_hdb_property?.additional_roles?.find(item => item.uinfin === nric);
    const structure_order = `L${props.householdFormValues.incoming_owners.length + 1}`;
    
    const resetResidentialCode = [ "U" , "A"];
    if (nric) {
      if (props.holdingSetFormValue) {
        if (foundUser) {
          
          let marital_status = "";
          if(Object.values(MaritalStatusEnum).includes(foundUser.myinfo.marital_status as MaritalStatusEnum)){
            marital_status = foundUser.myinfo.marital_status;
          }
         
          let residential_status = foundUser.myinfo.residential_status;
          if(resetResidentialCode.includes(foundUser.myinfo.residential_status)){
            residential_status = "";
          }

          props.holdingSetFormValue({
            ...props.holdingFormValues,
            holding_owners: [...temp, new HoldingOwner({ ...tempHoldingOwner, ...foundUser, myinfo: { ...tempHoldingOwner.myinfo, ...foundUser?.myinfo, uinfin: "", dob: "", marital_status, residential_status }, type: "existing", existing_type: "owner", id: Helpers.generateAlphaNumericID(), structure_order:structure_order, original_uinfin: nric })]
          });
        }
        else if (foundOccupierUser) {

          let marital_status = "";
          if(Object.values(MaritalStatusEnum).includes(foundOccupierUser.myinfo.marital_status as MaritalStatusEnum)){
            marital_status = foundOccupierUser.myinfo.marital_status;
          }
          
          let residential_status = foundOccupierUser.myinfo.residential_status;
          if(resetResidentialCode.includes(foundOccupierUser.myinfo.residential_status)){
            residential_status = "";
          }

          props.holdingSetFormValue({
            ...props.holdingFormValues,
            holding_owners: [...temp, new HoldingOwner({ ...tempHoldingOwner, ...foundOccupierUser, myinfo: { ...tempHoldingOwner.myinfo, ...foundOccupierUser?.myinfo, uinfin: "", dob: "", marital_status, residential_status }, type: "existing",existing_type: "occupier", id: Helpers.generateAlphaNumericID(), structure_order:structure_order, original_uinfin: nric })]
          });
        }
        else if (foundAdditionalRole) {

          let marital_status = "";
          if(Object.values(MaritalStatusEnum).includes(foundAdditionalRole.marital_status as MaritalStatusEnum)){
            marital_status = foundAdditionalRole.marital_status;
          }
          
          let residential_status = foundAdditionalRole.residential_status;
          if(resetResidentialCode.includes(foundAdditionalRole.residential_status)){
            residential_status = "";
          }

          props.holdingSetFormValue({
            ...props.holdingFormValues,
            holding_owners: [...temp, new HoldingOwner({ ...tempHoldingOwner, ...foundAdditionalRole, myinfo: { ...tempHoldingOwner.myinfo, ...foundAdditionalRole, uinfin: "", dob: "", marital_status, residential_status }, type: "existing" ,existing_type: "additional", id: Helpers.generateAlphaNumericID(), structure_order:structure_order, original_uinfin: nric })]
          });
        }
      }
    }
  }, [props, applicationStartFormData])


  const onBindOwnerMyInfoToHolding = useCallback((nric: string, myInfoData:MyInfo) => {
    if(!props.holdingSetFormValue){
      return;
    }
    if(props.currentOwnerMyInfoData){
      let newHoldingOwners = props.holdingFormValues?.holding_owners.map(e=>{
        if(e.original_uinfin === nric){
          const mergedMyInfo = Helpers.mergeMyInfoData(e.myinfo, myInfoData);

          console.log("DEBUG - MYINFO", mergedMyInfo);

          let new_holding_owner:HoldingOwner = new HoldingOwner({...e, myinfo: mergedMyInfo});
          return new_holding_owner;
        }
        else{
          return e;
        }
      })
      props.holdingSetFormValue({...props.holdingFormValues, holding_owners: [...newHoldingOwners]});
    }
    
  },[props]);

  const onUnBindOwnerMyInfoToHolding = useCallback((nric: string) => {
    let foundUser: Owner | undefined = props?.householdFormValues?.owners?.find(e => e.myinfo?.uinfin === nric);
    const foundOccupier: Occupier | undefined = props?.householdFormValues?.occupiers?.find(e => e.myinfo?.uinfin === nric);
    const foundAdditionalRoleMyInfo: MyInfoWithDOD | undefined = applicationStartFormData?.selected_hdb_property?.additional_roles?.find(e => e.uinfin === nric);

    if(!foundUser && (foundOccupier || foundAdditionalRoleMyInfo)) {
        foundUser = new Owner({myinfo: foundOccupier?.myinfo ?? foundAdditionalRoleMyInfo})
    }
    if(!props.holdingSetFormValue){
      return;
    }
    if(props.currentOwnerMyInfoData){
      let newHoldingOwners = props.holdingFormValues?.holding_owners.map(e=>{
        if(e.original_uinfin === nric){
          let new_holding_owner:HoldingOwner = new HoldingOwner({...e, myinfo: new MyInfo({...foundUser?.myinfo, uinfin:"", dob:"", marital_status:"", residential_status:""})});
          return new_holding_owner;
        }
        else{
          return e;
        }
      })
      props.holdingSetFormValue({...props.holdingFormValues, holding_owners: [...newHoldingOwners]});
    }
  },[props, applicationStartFormData]);
  
  const onBindOccupierMyInfoToHolding = useCallback((nric: string, myInfoData:MyInfo) => {
    if(!props.holdingSetFormValue){
      return;
    }
    if(props.currentOwnerMyInfoData){
      let newHoldingOccupiers = props.holdingFormValues?.holding_occupiers.map(e=>{
        if(e.original_uinfin === nric){

          const myInfo = new MyInfo({...myInfoData, dob: myInfoData.dob})
          console.log("DEBUG - MYINFO OCCUPIER", myInfo);
          let new_holding_occupier:HoldingOccupier = new HoldingOccupier({...e, myinfo: {...myInfo}});
          return new_holding_occupier;
        }
        else{
          return e;
        }
      })
      props.holdingSetFormValue({...props.holdingFormValues, holding_occupiers: [...newHoldingOccupiers]});
    }
    
  },[props]);
  
  const onUnBindOccupierMyInfoToHolding = useCallback((nric: string) => {
    let foundUser: Owner | undefined = props?.householdFormValues?.owners?.find(e => e.myinfo?.uinfin === nric);
    const foundOccupier: Occupier | undefined = props?.householdFormValues?.occupiers?.find(e => e.myinfo?.uinfin === nric);
    const foundAdditionalRoleMyInfo: MyInfoWithDOD | undefined = applicationStartFormData?.selected_hdb_property?.additional_roles?.find(e => e.uinfin === nric);

    if(!foundUser && (foundOccupier || foundAdditionalRoleMyInfo)) {
        foundUser = new Owner({myinfo: foundOccupier?.myinfo ?? foundAdditionalRoleMyInfo})
    }
    if(!props.holdingSetFormValue){
      return;
    }
    if(props.currentOwnerMyInfoData){
      let newHoldingOccupiers = props.holdingFormValues?.holding_occupiers.map(e=>{
        if(e.original_uinfin === nric){
          let new_holding_occupier:HoldingOccupier = new HoldingOccupier({...e, myinfo: {...foundUser?.myinfo, uinfin:"", dob:"", marital_status:"", residential_status:"" }});
          return new_holding_occupier;
        }
        else{
          return e;
        }
      })
      props.holdingSetFormValue({...props.holdingFormValues, holding_occupiers: [...newHoldingOccupiers]});
    }
  },[props, applicationStartFormData]);
  
  const addNewOwnerHolding = useCallback(() => {
  
     let tempHoldingOwner: HoldingOwner = new HoldingOwner();
     let temp = props.holdingFormValues?.holding_owners.filter(e => e.is_proposed === true); //to remove any unconfirmed box
     const structure_order = `L${props.householdFormValues.incoming_owners.length + 1}`;
 
    if (props.holdingSetFormValue) {
      props.holdingSetFormValue({
        ...props.holdingFormValues,
        holding_owners: [...temp, new HoldingOwner({ ...tempHoldingOwner, myinfo: { ...tempHoldingOwner.myinfo }, type: "new", id: Helpers.generateAlphaNumericID(), structure_order:structure_order })]
      });
    }
  }, [props])

  const addOccupierHolding = useCallback((nric: string) => {
    /**
     * Value can be uinfin or add_new
     * - add_new will add the add_new card fields to users to enter info
     */
    let tempHoldingOccupier: HoldingOccupier = new HoldingOccupier();
    let foundUser: Occupier | undefined = props?.householdFormValues?.occupiers?.find(e => e.myinfo?.uinfin === nric);
    let foundOwnerUser: Owner | undefined = props?.householdFormValues?.owners?.find(e => e.myinfo?.uinfin === nric);
    let temp = props.holdingFormValues?.holding_occupiers.filter(e => e.is_proposed === true); //to remove any unconfirmed box
    let foundAdditionalRole: MyInfoWithDOD | undefined = applicationStartFormData?.selected_hdb_property?.additional_roles?.find(item => item.uinfin === nric);
    if (nric) {
      if (props.holdingSetFormValue) {
        if (foundUser) {
          props.holdingSetFormValue({
            ...props.holdingFormValues,
            holding_occupiers: [...temp, new HoldingOccupier({ ...tempHoldingOccupier, ...foundUser, myinfo: { ...tempHoldingOccupier.myinfo, ...foundUser?.myinfo, uinfin: "", dob: "" }, type: "existing", existing_type: "occupier", id: Helpers.generateAlphaNumericID(), original_uinfin: nric })]
          });
        }
        else if (foundOwnerUser) {
          props.holdingSetFormValue({
            ...props.holdingFormValues,
            holding_occupiers: [...temp, new HoldingOccupier({ ...tempHoldingOccupier, ...foundOwnerUser, myinfo: { ...tempHoldingOccupier.myinfo, ...foundOwnerUser?.myinfo, uinfin: "", dob: "" }, type: "existing", existing_type: "owner", id: Helpers.generateAlphaNumericID(), original_uinfin: nric })]
          });
        }
        else if (foundAdditionalRole) {
          props.holdingSetFormValue({
            ...props.holdingFormValues,
            holding_occupiers: [...temp, new HoldingOccupier({ ...tempHoldingOccupier, ...foundAdditionalRole, myinfo: { ...tempHoldingOccupier.myinfo, ...foundAdditionalRole, uinfin: "", dob: "" }, type: "existing", existing_type: "additional", id: Helpers.generateAlphaNumericID(), original_uinfin: nric })]
          });
        }
      }
    }
    
  }, [props, applicationStartFormData])

  const addNewOccupierHolding = useCallback(() => {
    let tempHoldingOccupier: HoldingOccupier = new HoldingOccupier();
    let temp = props.holdingFormValues?.holding_occupiers.filter(e => e.is_proposed === true); //to remove any unconfirmed box

    if (props.holdingSetFormValue) {
      props.holdingSetFormValue({
        ...props.holdingFormValues,
        holding_occupiers: [...temp, new HoldingOccupier({ ...tempHoldingOccupier, myinfo: { ...tempHoldingOccupier.myinfo }, type: "new", id: Helpers.generateAlphaNumericID() })]
      });
    }
  }, [props])

  const notApplicableOccupierHolding = useCallback(() => {
    if (props.holdingSetFormValue) {
      props.holdingSetFormValue({ ...props.holdingFormValues, holding_occupiers: [] })
    }
    if (props.householdSetFormValue) {
      props.householdSetFormValue({ ...props.householdFormValues, incoming_occupiers: [] });
    }
  }, [props])


  const handleOccupierOnAdd = useCallback((e:HoldingOccupier)=>()=>{
    onAddHouseholdIncomingOccupier(e);
    let tempHoldingOccupiers = props.holdingFormValues?.holding_occupiers;
    let newObj = tempHoldingOccupiers?.map((holding_occupier) => {
      if (e.id === holding_occupier.id) {
        return { ...e, is_proposed: true, original_uinfin: e.myinfo.uinfin };
      } else {
        return { ...holding_occupier, original_uinfin: e.myinfo.uinfin};
      }
    });
    if (props.holdingSetFormValue) {
      props.holdingSetFormValue({
        ...props.holdingFormValues,
        holding_occupiers: newObj,
      });
    }
  },[onAddHouseholdIncomingOccupier, props])

 
  const handleOccupierOnUpdate = useCallback((e:HoldingOccupier)=>()=>{
    onUpdateHouseholdIncomingOccupier(e);
  },[onUpdateHouseholdIncomingOccupier])

  const handleOccupierOnToggle = useCallback((e:HoldingOccupier)=>(isOpen: boolean)=>{
    handleOnToggleOccupier(isOpen, e.id);
  },[handleOnToggleOccupier])
  



  const handleOwnerOnAdd = useCallback((e:HoldingOwner)=>()=>{
    onAddHouseholdIncomingOwner(e);
    let tempHoldingOwners = props.holdingFormValues?.holding_owners;
    let newObj = tempHoldingOwners?.map((holding_owner) => {
      if (e.id === holding_owner.id) {
        return { ...e, is_proposed: true, original_uinfin: e.myinfo.uinfin };
      } else {
        return { ...holding_owner, original_uinfin: e.myinfo.uinfin };
      }
    });
    if (props.holdingSetFormValue) {
      props.holdingSetFormValue({
        ...props.holdingFormValues,
        holding_owners: newObj,
      });
    }
  },[onAddHouseholdIncomingOwner, props])


  const handleOwnerOnUpdate = useCallback((e:HoldingOwner)=>()=>{
    onUpdateHouseholdIncomingOwner(e);
  },[onUpdateHouseholdIncomingOwner])

  const handleOwnerOnToggle = useCallback((e:HoldingOwner)=>(isOpen: boolean)=>{
    handleOnToggleOwner(isOpen, e.id);
  },[handleOnToggleOwner])
  

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  },[]);
  
  const handlePrimaryButton = useCallback(() => {
    closeModal();
    if (accordionType === "owner") {
      onRemoveOwnerHolding(deleteOwner);
    } else if (accordionType === "occupier") {
      onRemoveOccupierHolding(deleteOwner);
    }
  },[accordionType, closeModal, deleteOwner, onRemoveOccupierHolding, onRemoveOwnerHolding]);
  

  const handleSecondaryButton = useCallback(() => {
    closeModal();
  },[closeModal]);

  const hasCurrentOwner = props.householdFormValues.owners.length > 0;
  const incoming_owners = props.householdFormValues.incoming_owners;
  const modalMessage =
    currentOwner === 0
      ? `Are you sure you want to remove ${getOwnerName} from the proposed household? You will need to reselect the entire proposed household.`
      : `Are you sure you want to remove ${getOwnerName} from the proposed household?`;

  const proposed_owner_nric = props.householdFormValues.incoming_owners.map(e=>e.myinfo.uinfin);
  const proposed_occupier_nric = props.householdFormValues.incoming_occupiers.map(e=>e.myinfo.uinfin);
  const initialHouseholdNrics = props.householdFormValues.owners.map(item => item.myinfo.uinfin).concat(props.householdFormValues.occupiers.map(item => item.myinfo.uinfin));

  return (
    <div>
      <Separator variant="solid" style={{ margin: "32px 0" }} />

      <div className="px-4">
        <P2TextDescription color="#000" title="Ownership Change Details" />
        <div ref={anchorRef}></div>
        <HouseholdStructureTable
          owners={props.householdFormValues?.owners}
          incomingOwners={props.householdFormValues?.incoming_owners}
          incomingOccupiers={props.householdFormValues?.incoming_occupiers}
          occupiers={props.householdFormValues?.occupiers}
          additionalRoles={applicationStartFormData.selected_hdb_property?.additional_roles}
        />

        <div>
          <div style={{display: 'flex', gap: '4px', flexDirection: 'column'}}>
            <P2TextDescription
              required
              color="#000"
              title="Selection of owners and occupiers"
            />
            <P2TextLabel
             label={<span style={{fontWeight: 400, fontSize: "1.125rem"}}>Please select owners and occupiers from the dropdown list below to include in the proposed household column. Owners and occupiers selected will be displayed in the summary table above.</span>}
              bold={400}
              hasTooltip={DynamicDescription === "" ? false : true}
              tooltipContent={DynamicDescription}
            />
          </div>
          <div style={{ marginTop: 32 }}>
            <OwnerOccupierSelection
              label="Add owners to proposed household"
              variant="owner"
              placeholder="Please select the owner(s) to add"
              limit={4}
              householdFormData={props.householdFormValues}
              holding={props.holdingFormValues?.holding_owners.concat(props.holdingFormValues?.holding_occupiers as any)}
              selection_holding_number={props.holdingFormValues?.holding_owners.length}
              onSelection={addOwnerHolding}
              onAddNewClick={addNewOwnerHolding}
              additionalRoles={applicationStartFormData?.selected_hdb_property?.additional_roles ?? []}
            />
          </div>
        </div>
        <TransitionGroup className="polymer-household-structure-accordion">
          {props.holdingFormValues?.holding_owners?.map((e, _index) => {
            let isDefaultOpen = defaultCloseArr.includes(e.id ? e.id : "");
            const isMyInfoVariant = props.currentOwnerMyInfoData?.uinfin === e.original_uinfin && props.retrieveFromMyInfo === true;
            return (
              <CSSTransition key={`holding_incoming_owners_${e.id}`} nodeRef={ownerNodeRef} timeout={300} classNames="polymer-household-structure-accordion">
                <NewOwnerAccordion
                  loginUserStructureStatus={props.loginUserStructureStatus}
                  proposed_nric={proposed_owner_nric.concat(proposed_occupier_nric)}
                  householdFormValues={props.householdFormValues}
                  isMyInfoVariant={isMyInfoVariant}
                  index={_index}
                  isDefaultOpen={!isDefaultOpen}
                  item={e}
                  reasonValues={props.reasonValues}
                  holdingIncomingOwnerHandler={holdingIncomingOwnerHandler}
                  onToggle={handleOwnerOnToggle(e)}
                  onUpdate={handleOwnerOnUpdate(e)}
                  onAdd={handleOwnerOnAdd(e)}
                  onBindMyInfo={onBindOwnerMyInfoToHolding}
                  onUnBindMyInfo={onUnBindOwnerMyInfoToHolding}
                  onRemove={createOnRemoveHandler(_index, e.myinfo?.name, 'owner')}
                  singpassToken={singpassToken}
                  forceError={props.forceError}
                  hasCurrentOwner={hasCurrentOwner}
                  initialHouseholdNrics={initialHouseholdNrics}
                />
              </CSSTransition>
            );
          })}
        </TransitionGroup>
        <div className="polymer-large-width">
          <P2Modal
            isOpen={isModalOpen}
            onCloseModal={closeModal}
            variant="default"
            title={`Remove ${getOwnerName}`}
            primaryButtonText={`Yes, remove ${getOwnerName}`}
            secondaryButtonText="No, do not remove"
            primaryButtonFunction={handlePrimaryButton}
            secondaryButtonFunction={handleSecondaryButton}
            children={<p>{modalMessage}</p>}
          />
        </div>

        <div style={{ marginTop: 32 }}>
          <OwnerOccupierSelection
            label="Add occupiers to proposed household"
            disabled={props.householdFormValues.incoming_owners.length === 0}
            variant="occupier"
            placeholder="Please select the occupier(s) to add (if applicable)"
            limit={10}
            householdFormData={props.householdFormValues}
            holding={props.holdingFormValues?.holding_owners.concat(props.holdingFormValues?.holding_occupiers as any)}
            selection_holding_number={props.holdingFormValues?.holding_occupiers.length}
            onSelection={addOccupierHolding}
            onAddNewClick={addNewOccupierHolding}
            onNotApplicableClick={notApplicableOccupierHolding}
            additionalRoles={applicationStartFormData?.selected_hdb_property?.additional_roles ?? []}
          />
        </div>
        <TransitionGroup className="polymer-household-structure-accordion">
          {props.holdingFormValues?.holding_occupiers?.map((e, _index) => {
            let isDefaultOpen = defaultCloseOccupiersArr.includes(e.id ? e.id : "");
            const isMyInfoVariant = props.currentOwnerMyInfoData?.uinfin === e.original_uinfin && props.retrieveFromMyInfo === true;
            return (
              <CSSTransition key={`holding_incoming_owners_${e.id}`} nodeRef={occupierNodeRef} timeout={300} classNames="polymer-household-structure-accordion">
                <NewOccupierAccordion
                  proposed_nric={proposed_owner_nric.concat(proposed_occupier_nric)}
                  incoming_owners={incoming_owners}
                  isMyInfoVariant={isMyInfoVariant}
                  index={_index}
                  isDefaultOpen={!isDefaultOpen}
                  item={e}
                  reasonValues={props.reasonValues}
                  holdingIncomingOccupierHandler={holdingIncomingOccupierHandler}
                  onToggle={handleOccupierOnToggle(e)}
                  onUpdate={handleOccupierOnUpdate(e)}
                  onAdd={handleOccupierOnAdd(e)}
                  onBindMyInfo={onBindOccupierMyInfoToHolding}
                  onUnBindMyInfo={onUnBindOccupierMyInfoToHolding}
                  onRemove={createOnRemoveHandler(_index, e.myinfo?.name, 'occupier')}
                  singpassToken={singpassToken}
                  forceError={props.forceError}
                  initialHouseholdNrics={initialHouseholdNrics}
                />
              </CSSTransition>
            );
          })}
        </TransitionGroup>
      </div>
    </div>
  );
}

export default HouseholdStructure

